import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, Chip, EmptyState, Skeleton } from '../ui'
import type { ChipProps } from '../ui'
import { ApiError } from '../../api/client'
import { useMappingBoard, useMappingBulkCreate, useMappingBulkQuickDecide, useMappingDecide, useMappingSites, useMappingSummary } from '../../api/hooks'
import type { MappingBulkFailure, QuickDecidePlan } from '../../api/hooks'
import type { AccessSystem, MappingItem, MappingSite, MappingState, MatchHint } from '../../api/types'
import { kindLabels } from './MappingCard'
import { DOOR_KIND_LABELS, candidateDoorKind } from '../../pages/Doors'
import { canCreate, createAtFields, createSeriesAtFields, defaultCreateFields, MappingCreateForm } from './MappingCreateForm'
import {
  exactAttachHints as attachHintsFor,
  exactLocationHints as locationHintsFor,
  exactSeriesAddHints as seriesAddHintsFor,
  exactSeriesAtHints as seriesAtHintsFor,
  quickDecideFor,
} from './mappingQuick'
import { attachTarget, isMergeTarget, MappingAttachPicker } from './MappingAttachPicker'
import { DecisionRunModal } from './DecisionRunModal'
import { SplitOnTool } from './SplitOnTool'
import { undecidedTotal } from './MappingCallout'

/** Per-kind purpose blurbs for the wizard rail. The STEP LIST ITSELF is
 *  data-driven: the backend's board/summary emits exactly the connector's
 *  adoptable_kinds in the canonical dependency order
 *  (core/mapping.py KIND_ORDER), so a system only ever shows the steps its
 *  provider actually has — the console never hardcodes provider vocab. */
const stepPurposes: Record<string, string> = {
  group: 'Folders become location trees and groups — split names on a separator, or decide rows one by one.',
  location: 'Places and structures from the vendor.',
  device: 'Place the hardware next — doors and policies hang off placed devices.',
  zone: 'Alarm and rollcall zones from the vendor.',
  door: 'Doors get placed on locations.',
  schedule: 'Time grids — door policies and door codes reference them.',
  door_code: 'Keypad codes get homed on locations.',
  access_group: 'Door policies derive once doors are placed on locations.',
  new_credential: 'Badged but unlinked — convert to a holder, attach to an existing credential, or ignore.',
  credential: 'Cards and codes, ready to hand to people.',
  person: 'People come last — they reference everything above.',
}

const stateTones: Record<MappingState, ChipProps['tone']> = {
  undecided: 'warn',
  skipped: 'muted',
  bound: 'ok',
}

/** Unknown-credential sightings (new IDs badged at a reader) ride the board
 *  as credential candidates, but converting one needs a per-row person —
 *  they never join the bulk create. */
export const isSightedCandidate = (item: MappingItem) => item.vendor_uid.startsWith('ouc-')

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

/** One site tab of the multi-site switcher, with its progress inline
 *  (`Site One · 2 to map`, a check when nothing is left). Same pill idiom
 *  as the Events page severity filter. */
function SitePill({
  site,
  active,
  onClick,
}: {
  site: MappingSite
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-sm border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors focus:outline-none focus:ring-2 focus:ring-ostrel-gold ${
        active
          ? 'border-ostrel-navy bg-ostrel-navy text-ostrel-light'
          : 'border-ostrel-line bg-ostrel-card text-ostrel-ink-muted hover:bg-ostrel-sand'
      }`}
    >
      {site.label}
      {site.undecided > 0 ? ` · ${site.undecided} to map` : ' ✓'}
    </button>
  )
}

/** The useful vendor detail fields: primitives only, vendor-internal keys
 *  skipped, capped so a row stays scannable. The sightings' `attempts`
 *  breakdown (an array of 'place × N') always survives the cap — it's the
 *  whole point of the New credentials review. */
function detailEntries(item: MappingItem): [string, string][] {
  const out: [string, string][] = []
  let attempts: string | null = null
  for (const [key, value] of Object.entries(item.detail)) {
    if (key === 'attempts' && Array.isArray(value)) {
      if (value.length > 0) attempts = value.join(' · ')
      continue
    }
    if (out.length >= 4) continue
    if (value === null || value === undefined || value === '') continue
    if (typeof value === 'string' || typeof value === 'number') {
      out.push([key.replace(/_/g, ' '), String(value)])
    } else if (typeof value === 'boolean') {
      out.push([key.replace(/_/g, ' '), value ? 'yes' : 'no'])
    }
  }
  if (attempts) out.push(['tried at', attempts])
  return out
}

/** One row of a sighting's per-place attempt breakdown (backend:
 *  core.mapping._sighting_attempts_by_place). */
interface AttemptRow {
  place: string
  count: number
  last: string
}

/** The unknown-tag (new_credential) row's detail: stats on one line,
 *  then the per-place attempts as a table — last-tried first, every
 *  row a panel (mailbox sweeps collapse per panel, the panel name
 *  carries the address) or a single reader. */
function SightingDetail({ item }: { item: MappingItem }) {
  const d = item.detail
  const rows = Array.isArray(d.attempts) ? (d.attempts as AttemptRow[]) : []
  return (
    <div className="flex flex-col gap-1.5">
      <p className="tnum text-[11px] text-ostrel-ink-muted">
        seen {String(d.seen_count ?? 0)}× · last {String(d.last_seen ?? '—')}
        {typeof d.code === 'string' && d.code !== item.display ? ` · code ${d.code}` : ''}
      </p>
      {rows.length > 0 && (
        <table className="w-full table-fixed border-collapse text-[11px]">
          <thead>
            <tr className="text-left text-[9px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              <th className="w-36 pr-3 font-semibold">Last tried</th>
              <th className="pr-3 font-semibold">Door</th>
              <th className="w-12 text-right font-semibold">Times</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.place} className="text-ostrel-ink-muted">
                <td className="tnum whitespace-nowrap pr-3">{row.last}</td>
                <td className="truncate pr-3" title={row.place}>{row.place}</td>
                <td className="tnum text-right">{row.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/** The 100%-suggestion quick action: one hit renders a single primary
 *  button that fires its decide straight away (the score IS the confidence
 *  signal — no confirm modal); several hits render a `verb ▾` dropdown
 *  listing each (display + score). */
function QuickDecideButtons({
  verb,
  pendingVerb,
  hints,
  pending,
  onPick,
}: {
  /** 'Attach to' | 'Merge into' | 'Create at' — the single-hit label is
   *  `{verb} {display}`. */
  verb: string
  pendingVerb: string
  hints: MatchHint[]
  pending: boolean
  onPick: (hint: MatchHint) => void
}) {
  const [open, setOpen] = useState(false)
  if (hints.length === 0) return null
  if (hints.length === 1) {
    return (
      <Button variant="primary" size="sm" disabled={pending} onClick={() => onPick(hints[0])}>
        <span className="max-w-56 truncate">
          {pending ? pendingVerb : `${verb} ${hints[0].display || hints[0].canonical_uid}`}
        </span>
      </Button>
    )
  }
  return (
    <span className="relative">
      <Button
        variant="primary"
        size="sm"
        disabled={pending}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {pending ? pendingVerb : `${verb} ▾`}
      </Button>
      {open && (
        <div className="absolute top-full right-0 z-20 mt-1 w-64 overflow-hidden rounded-sm border border-ostrel-line bg-ostrel-card shadow-xl">
          {hints.map((h) => (
            <button
              key={h.canonical_uid}
              type="button"
              onClick={() => {
                setOpen(false)
                onPick(h)
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-ostrel-ink transition-colors hover:bg-ostrel-sand focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
            >
              <span className="min-w-0 flex-1 truncate">{h.display || h.canonical_uid}</span>
              <span className="tnum shrink-0 text-ostrel-ink-muted">{Math.round(h.score * 100)}%</span>
            </button>
          ))}
        </div>
      )}
    </span>
  )
}

/** One vendor object: display, ids, key detail fields, decision state, and
 *  (on open rows) the ranked match hints. Bound rows carry only Unbind
 *  (removes the vendor link, keeps the canonical entity — the row returns
 *  to undecided).
 *  Undecided rows carry the decide actions — Skip, Attach/Merge
 *  (MappingAttachPicker, kinds with a canonical target only), plus the
 *  create pair where the provider/kind supports create (attach-only kinds
 *  don't, see MappingCreateForm.canCreate): ONE-CLICK Create with the
 *  form's untouched defaults (defaultCreateFields — hidden on rows with
 *  required input like a sighted convert's holder; rows that need the
 *  form's judgement, like a rotation-detected door_code, route the click
 *  to the form instead) and Custom… opening the editable form. Skipped
 *  rows offer Attach and Create again: a re-decide clears the dismissal
 *  backend-side. A suggestion at a rounded 100% score also gets a QUICK
 *  action (QuickDecideButtons — one hit fires the decide straight away,
 *  the score is the confidence signal; several hits open a dropdown):
 *  attach-target hints quick-attach/merge, location hints quick-create-at
 *  (createAtFields forces the location) — or quick-create-SERIES-at on
 *  rotation-detected door_codes (fields.series: true, no static shortcut). */
function CandidateRow({
  system,
  item,
  site,
  selectable = false,
  checked = false,
  onToggle,
}: {
  system: AccessSystem
  item: MappingItem
  /** The workbench's selected site — decide mutations patch the site-scoped
   *  board cache. */
  site?: string
  /** Bulk-create selection: a checkbox on undecided rows of creatable kinds. */
  selectable?: boolean
  checked?: boolean
  onToggle?: (vendorUid: string, next: boolean) => void
}) {
  const details = detailEntries(item)
  const skip = useMappingDecide(system.unique_id, site)
  const createOne = useMappingDecide(system.unique_id, site)
  const unbind = useMappingDecide(system.unique_id, site)
  const [createOpen, setCreateOpen] = useState(false)
  const [attachOpen, setAttachOpen] = useState(false)
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false)
  const [unbindConfirmOpen, setUnbindConfirmOpen] = useState(false)
  /** Suggestion-click prefills: a normal hint opens the Attach picker with
   *  its target pre-selected; a location hint opens Create-new with the
   *  location picker set. The operator still confirms — a click never
   *  decides straight away. */
  const [attachPrefill, setAttachPrefill] = useState<string | null>(null)
  const [createLocationPrefill, setCreateLocationPrefill] = useState<string | null>(null)
  const creatable = canCreate(system.provider, item.kind)
  const target = attachTarget(system.provider, item.kind)
  const attachable = target !== null
  const sighted = isSightedCandidate(item)
  /** One-click create: the exact fields the form would submit untouched
   *  (vendor name + manage stay backend-side defaults); null when the row
   *  needs the form — a sighted convert (holder required, Create hidden) or
   *  a rotation-detected door_code (Create routes to the form so the
   *  operator sees the series they're creating). */
  const oneClickFields = creatable ? defaultCreateFields(system, item) : null
  const showOneClick = creatable && !sighted
  /** Quick actions fire on (rounded) 100% suggestions — the score IS the
   *  confidence signal, no confirm modal. The hint sets are the shared
   *  mappingQuick helpers (the same sets the bulk "Decide 100% matches"
   *  runner prioritizes): attach-target hints quick-ATTACH, location hints
   *  quick-CREATE-AT, family hints quick-ADD-TO-series, and create-SERIES-at
   *  when no family hint exists. Sighted rows get none of them. */
  const exactAttachHints = attachHintsFor(system, item)
  const exactLocationHints = locationHintsFor(system, item)
  const exactSeriesAddHints = seriesAddHintsFor(item)
  const exactSeriesHints = seriesAtHintsFor(item)
  const quick = useMappingDecide(system.unique_id, site)
  const mergeWording = target !== null && isMergeTarget(target)
  const quickVerb = mergeWording ? 'Merge into' : 'Attach to'
  const quickFire = (hint: MatchHint) => {
    if (quick.isPending) return
    const action = item.kind === 'new_credential' ? 'attach' : mergeWording ? 'merge' : 'attach'
    quick.mutate({ kind: item.kind, vendor_uid: item.vendor_uid, action, target: hint.canonical_uid })
  }
  const createAtFire = (hint: MatchHint) => {
    if (quick.isPending) return
    const fields = createAtFields(system, item, hint.canonical_uid)
    if (fields === null) return
    quick.mutate({
      kind: item.kind,
      vendor_uid: item.vendor_uid,
      action: 'create',
      fields: Object.keys(fields).length > 0 ? fields : null,
    })
  }
  const createSeriesAtFire = (hint: MatchHint) => {
    if (quick.isPending) return
    const fields = createSeriesAtFields(item, hint.canonical_uid)
    if (fields === null) return
    quick.mutate({ kind: item.kind, vendor_uid: item.vendor_uid, action: 'create', fields })
  }
  const addToSeriesFire = (hint: MatchHint) => {
    if (quick.isPending) return
    // Attach to the existing series: fields.series is the UID here (not
    // true) — the hook's sibling-refetch branch checks === true, so this
    // correctly keeps the surgical single-row patch (no sweep ran).
    quick.mutate({
      kind: item.kind,
      vendor_uid: item.vendor_uid,
      action: 'create',
      fields: { series: hint.canonical_uid },
    })
  }
  return (
    <li className="flex flex-col gap-1.5 rounded-sm border border-ostrel-line bg-ostrel-card px-3.5 py-3">
      <div className="flex flex-wrap items-center gap-2">
        {selectable && !sighted && item.state === 'undecided' && (
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onToggle?.(item.vendor_uid, e.target.checked)}
            aria-label={`Select ${item.display}`}
            className="accent-ostrel-gold"
          />
        )}
        <span className="min-w-0 truncate text-sm font-medium text-ostrel-ink">{item.display}</span>
        {/* Door candidates: the kind the backend's create auto-detect will
            land on (detail.door_kind — model-informed — else the name
            rule; detection only). */}
        {item.kind === 'door' && candidateDoorKind(item) !== 'door' && (
          <Chip tone="info" label={DOOR_KIND_LABELS[candidateDoorKind(item)]} />
        )}
        {sighted && <Chip tone="warn" label="new ID" />}
        <Chip tone={stateTones[item.state]} label={item.state} />
        <span className="tnum ml-auto truncate font-mono text-[11px] text-ostrel-ink-muted">
          {item.vendor_uid}
        </span>
      </div>
      {sighted ? (
        <SightingDetail item={item} />
      ) : (
        details.length > 0 && (
          <p className="tnum text-[11px] text-ostrel-ink-muted">
            {details.map(([k, v]) => `${k}: ${v}`).join(' · ')}
          </p>
        )
      )}
      {item.state === 'bound' && (
        <div className="flex flex-wrap items-center gap-2">
          {item.outcome && <p className="tnum text-[11px] text-ostrel-ok">→ {item.outcome}</p>}
          {/* Device and door placements are edited on their canonical detail
              pages; link there so the workbench is also a relocation surface. */}
          {item.outcome && (item.kind === 'device' || item.kind === 'door') && (
            <Link
              to={`/${item.kind}s/${item.outcome}`}
              className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold hover:text-ostrel-accent"
            >
              Edit placement
            </Link>
          )}
          {/* Unbind removes the vendor link, keeps the canonical entity, and
              flips the row back to undecided (the decide response patches
              the board in place). A kind the connector can't unbind 400s —
              the error surfaces inline below. */}
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            disabled={unbind.isPending}
            onClick={() => setUnbindConfirmOpen(true)}
          >
            Unbind
          </Button>
        </div>
      )}
      {item.state === 'undecided' && item.suggestions.length > 0 && (
        <div className="rounded-sm border border-ostrel-line bg-ostrel-sand/40 px-3 py-2">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Suggestions
          </p>
          <ul className="flex flex-col gap-1">
            {item.suggestions.slice(0, 3).map((s) => {
              const locationHint = s.target_kind === 'location'
              // A hint is actionable when the form it would open exists:
              // location hints open Create-new, normal hints open Attach.
              const actionable = locationHint ? creatable : attachable
              const fields = (
                <>
                  <span className="tnum text-ostrel-ink-muted">{Math.round(s.score * 100)}%</span>
                  <span className="min-w-0 truncate text-ostrel-ink">
                    {s.display || s.canonical_uid}
                  </span>
                  <span className="min-w-0 truncate text-ostrel-ink-muted">{s.why}</span>
                  {s.display && (
                    <span className="tnum ml-auto truncate font-mono text-ostrel-ink-muted">
                      {s.canonical_uid}
                    </span>
                  )}
                </>
              )
              return (
                <li key={s.canonical_uid}>
                  {actionable ? (
                    <button
                      type="button"
                      title={locationHint ? 'Create new with this location' : 'Attach to this target'}
                      onClick={() => {
                        if (locationHint) {
                          setCreateLocationPrefill(s.canonical_uid)
                          setCreateOpen(true)
                          setAttachOpen(false)
                        } else {
                          setAttachPrefill(s.canonical_uid)
                          setAttachOpen(true)
                          setCreateOpen(false)
                        }
                      }}
                      className="flex w-full flex-wrap items-center gap-2 rounded-sm px-1 py-0.5 text-left text-[11px] transition-colors hover:bg-ostrel-sand focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
                    >
                      {fields}
                    </button>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2 px-1 py-0.5 text-[11px]">
                      {fields}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}
      {item.state !== 'bound' && (creatable || attachable || item.state === 'undecided') && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <QuickDecideButtons
            verb={quickVerb}
            pendingVerb={mergeWording ? 'Merging…' : 'Attaching…'}
            hints={exactAttachHints}
            pending={quick.isPending}
            onPick={quickFire}
          />
          <QuickDecideButtons
            verb="Create at"
            pendingVerb="Creating…"
            hints={exactLocationHints}
            pending={quick.isPending}
            onPick={createAtFire}
          />
          <QuickDecideButtons
            verb="Add to"
            pendingVerb="Adding…"
            hints={exactSeriesAddHints}
            pending={quick.isPending}
            onPick={addToSeriesFire}
          />
          <QuickDecideButtons
            verb="Create series at"
            pendingVerb="Creating…"
            hints={exactSeriesHints}
            pending={quick.isPending}
            onPick={createSeriesAtFire}
          />
          {attachable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setAttachOpen((v) => !v)
                setAttachPrefill(null)
                setCreateOpen(false)
              }}
            >
              {target !== null && isMergeTarget(target) ? 'Merge…' : 'Attach…'}
            </Button>
          )}
          {showOneClick && (
            <Button
              variant="primary"
              size="sm"
              disabled={createOne.isPending}
              onClick={() => {
                if (oneClickFields !== null) {
                  createOne.mutate({
                    kind: item.kind,
                    vendor_uid: item.vendor_uid,
                    action: 'create',
                    fields: Object.keys(oneClickFields).length > 0 ? oneClickFields : null,
                  })
                } else {
                  // Needs the form (rotation-detected door_code) — open it.
                  setCreateOpen(true)
                  setAttachOpen(false)
                }
              }}
            >
              {createOne.isPending ? 'Creating…' : 'Create'}
            </Button>
          )}
          {creatable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setCreateOpen((v) => !v)
                setCreateLocationPrefill(null)
                setAttachOpen(false)
              }}
            >
              Custom…
            </Button>
          )}
          {item.state === 'undecided' && (
            <Button
              variant="ghost"
              size="sm"
              disabled={skip.isPending}
              onClick={() => setSkipConfirmOpen(true)}
            >
              {/* A skipped sighting IS the triage 'ignore' — same decision. */}
              {sighted ? 'Ignore' : 'Skip'}
            </Button>
          )}
        </div>
      )}
      {createOne.error && (
        <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
          {errDetail(createOne.error)}
        </p>
      )}
      {quick.error && (
        <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
          {errDetail(quick.error)}
        </p>
      )}
      {unbind.error && !unbindConfirmOpen && (
        <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
          {errDetail(unbind.error)}
        </p>
      )}
      {item.state !== 'bound' && attachable && attachOpen && (
        <MappingAttachPicker
          system={system}
          item={item}
          site={site}
          initialSelected={attachPrefill ?? undefined}
          onDone={() => setAttachOpen(false)}
        />
      )}
      {item.state !== 'bound' && creatable && createOpen && (
        <MappingCreateForm
          system={system}
          item={item}
          site={site}
          initialLocationUid={createLocationPrefill ?? undefined}
          onDone={() => setCreateOpen(false)}
        />
      )}
      <DecisionRunModal
        open={skipConfirmOpen}
        title={sighted ? 'Ignore new ID' : 'Skip row'}
        confirmLabel={sighted ? 'Ignore' : 'Skip'}
        confirmText={
          sighted ? (
            <p>
              Ignore <span className="font-medium">{item.display}</span>? It stays under Ignored on the
              Credentials page — you can still convert it later from the skipped bucket.
            </p>
          ) : (
            <p>
              Skip <span className="font-medium">{item.display}</span>? It moves to the skipped bucket — you
              can re-decide it from there at any time.
            </p>
          )
        }
        log={[]}
        running={skip.isPending}
        error={skip.error ? errDetail(skip.error) : null}
        onConfirm={() =>
          skip.mutate(
            { kind: item.kind, vendor_uid: item.vendor_uid, action: 'skip' },
            { onSuccess: () => setSkipConfirmOpen(false) },
          )
        }
        onClose={() => setSkipConfirmOpen(false)}
      />
      <DecisionRunModal
        open={unbindConfirmOpen}
        title="Unbind row"
        confirmLabel="Unbind"
        confirmText={
          <p>
            Unbind <span className="font-medium">{item.display}</span>? The canonical entity stays —
            only the vendor link is removed, and the row returns to undecided so you can re-decide it.
          </p>
        }
        log={[]}
        running={unbind.isPending}
        error={unbind.error ? errDetail(unbind.error) : null}
        onConfirm={() =>
          unbind.mutate(
            { kind: item.kind, vendor_uid: item.vendor_uid, action: 'unbind' },
            { onSuccess: () => setUnbindConfirmOpen(false) },
          )
        }
        onClose={() => setUnbindConfirmOpen(false)}
      />
    </li>
  )
}

/** Workbench FAQ — plain-language Q&A for non-technical operators (board
 *  members, property managers). ONE list at module top, rendered
 *  default-collapsed under the step header; static content, no calls. */
const WORKBENCH_FAQ: { q: string; a: string }[] = [
  {
    q: 'What is this page? What am I deciding?',
    a: "Your door system (the vendor) holds a list of people, cards, doors and codes. This page helps you bring them into Ostrel, one category at a time. Nothing changes until you decide a row — looking is free.",
  },
  {
    q: 'What do Undecided, Skipped and Bound mean?',
    a: 'Undecided rows still need your decision. Bound rows are done — they are linked to an Ostrel object. Skipped rows are set aside; they wait in the collapsed Skipped bucket at the bottom and you can change your mind there.',
  },
  {
    q: 'What do Create, Attach, Custom and Skip do?',
    a: 'Create makes a new Ostrel object from the vendor row with sensible defaults. Attach links the row to an object you already have. Custom lets you create with your own choices (location, kind, and so on). Skip hides the row — it stays in the skipped bucket and can be brought back at any time.',
  },
  {
    q: 'What does "Decide 100% matches" do?',
    a: 'It decides all selected rows whose top suggestion is a full-confidence (100%) match, in one go with live progress. Rows without a perfect match are left untouched for you to review.',
  },
  {
    q: 'What do "Select all" and "Create N selected" do?',
    a: 'They create new Ostrel objects for everything you selected as one batched action, with a live log of what succeeded. Keep this page open while the batch runs — it stops if you leave.',
  },
  {
    q: 'Why are some steps locked?',
    a: 'The steps build on each other: locations and groups first, then devices, then doors, and people last. A step unlocks once every earlier step is fully decided or skipped.',
  },
  {
    q: 'I made a mistake — can I undo it?',
    a: 'Yes. Open the bound row and choose Unbind — it goes back to undecided so you can decide again. The Ostrel object stays, and nothing is ever deleted on the vendor side.',
  },
  {
    q: 'Are the suggestions automatic?',
    a: 'No. The percentage on each suggestion is just a name-similarity hint from the system. You always make the final call — 100% simply means the names matched exactly.',
  },
  {
    q: 'What is a door "kind" (door / mailbox / trash)?',
    a: 'A simple classification guessed from the device name: names starting with PB become mailboxes, names containing sug/sopor/trash become trash rooms, everything else is a door. You can override it in the create form or later on the Doors page.',
  },
  {
    q: 'Where do decided things show up?',
    a: 'In the sidebar: people under People, groups under Groups, openings under Doors, codes under Door codes, and places under Locations. Access rules end up under Access groups.',
  },
]

/**
 * The mapping guide as an ORDERED WIZARD for one System (spec §3 round 2):
 *  the candidate kinds are walked in a fixed dependency order (Groups →
 *  Devices → Doors → Access groups → Credentials → People) and the order is
 *  enforced, not suggested — a step stays locked until every earlier step
 *  holds zero undecided candidates (decided or skipped both count). The left
 *  rail is the step list (number, label, decided/total, Locked chip); a
 *  progress header shows "Step N of M" plus the overall undecided count.
 *  Each step reuses the same row/decide machinery: Skip (undecided),
 *  Attach/Merge (MappingAttachPicker), Create-new (MappingCreateForm), the
 *  checkbox bulk create (ONE decide-bulk call, failures stay selected for
 *  retry), and — on the Groups step — the SplitOnTool tree import. The
 *  confirm modal renders a snapshot of the selection taken at open time, so
 *  a board refetch while it sits open can't empty the names list.
 *  The board read 400s (capability_not_supported) for providers without
 *  SupportsAdoption — a provider verdict, rendered as a dedicated empty state
 *  (the hook never retries it).
 *  Multi-site providers (techfass, came) reuse names across sites, so the
 *  wizard always operates on ONE site at a time: a switcher of site pills
 *  (label + progress) sits on top, every board read and every mutation
 *  cache patch carries the selected site, and switching sites keeps
 *  the wizard on the same step kind. Single-site providers report no sites —
 *  no switcher, unfiltered board, exactly as before.
 */
export function MappingWorkbench({ system, onClose }: { system: AccessSystem; onClose: () => void }) {
  // The site dimension: [] (or a 400 verdict) means a single-site provider —
  // no switcher and the board stays unfiltered. Default selection: the first
  // site still holding undecided rows, else the first site.
  const sites = useMappingSites(system.unique_id)
  const siteList = sites.data ?? []
  const [selectedSite, setSelectedSite] = useState<string | null>(null)
  const activeSite =
    siteList.find((s) => s.site_key === selectedSite) ??
    siteList.find((s) => s.undecided > 0) ??
    siteList[0]
  const site = siteList.length > 0 ? activeSite?.site_key : undefined

  // The board WAITS for the sites query to settle: firing it while sites
  // are still loading would build the (server-side expensive) board TWICE
  // on multi-site systems — once unfiltered, once with ?site= after the
  // default site resolves. The per-kind counts (step rail, "N to map") are
  // derived from the board items below; the summary endpoint is never
  // called from the workbench.
  const board = useMappingBoard(sites.isPending ? undefined : system.unique_id, site)
  const [selectedKind, setSelectedKind] = useState<string | null>(null)
  const [skipOpen, setSkipOpen] = useState(false)
  const [faqOpen, setFaqOpen] = useState(false)

  // Bulk create: checkbox selection on undecided rows of the active step,
  // one confirm modal, then ONE decide-bulk call (useMappingBulkCreate).
  const bulk = useMappingBulkCreate(system.unique_id, system.provider, site)
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set())
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false)
  /** Frozen snapshot of the selection taken when the confirm modal opens.
   *  The modal renders and the bulk call sends THESE rows — the candidates
   *  are computed live, so a board refetch while the modal sits open (sync
   *  deletion, a concurrent decide) would otherwise empty the names list
   *  under the operator. */
  const [bulkTargets, setBulkTargets] = useState<MappingItem[]>([])
  const [bulkFailures, setBulkFailures] = useState<MappingBulkFailure[]>([])

  /** The wizard steps: exactly the kinds the board reports (the connector's
   *  adoptable_kinds), in the backend's canonical dependency order — a
   *  system with no schedule candidates has no Schedules step at all. Each
   *  step carries its counts and lock state: locked while ANY earlier step
   *  still has undecided candidates. */
  const steps = useMemo(() => {
    const data = board.data ?? {}
    let earlierUndecided = 0
    return Object.keys(data)
      .map((kind) => ({ kind, purpose: stepPurposes[kind] ?? 'Additional vendor objects.' }))
      .map((step) => {
        const items = data[step.kind] ?? []
        const decided = items.filter((i) => i.state !== 'undecided').length
        const undecided = items.length - decided
        const locked = earlierUndecided > 0
        earlierUndecided += undecided
        return { ...step, items, decided, undecided, locked }
      })
  }, [board.data])

  const totalUndecided = steps.reduce((sum, s) => sum + s.undecided, 0)
  // The header chip names BOTH counts when they diverge: the site-scoped
  // board can undercount the whole system (sightings carry no site), so a
  // bare '1 to map' next to the dashboard's '46 objects to map' read as a
  // bug — '1 in this step · 46 total' says exactly what each number is.
  const summary = useMappingSummary(system.unique_id)
  const systemUndecided = summary.data ? undecidedTotal(summary.data) : totalUndecided

  // A split run keeps its step pinned: applying the last rows flips the
  // step to zero undecided, which would otherwise rotate the wizard away
  // and unmount the tool — and its run-log modal — mid-flow.
  const [splitBusy, setSplitBusy] = useState(false)
  const lastActiveKind = useRef<string | null>(null)

  // Default selection: the first step still holding undecided rows (always
  // unlocked by construction), else step 1. An explicit unlocked rail click
  // always wins; a selection that a refetch locked falls back.
  const active =
    (splitBusy ? steps.find((s) => s.kind === lastActiveKind.current) : undefined) ??
    steps.find((s) => s.kind === selectedKind && !s.locked) ??
    steps.find((s) => s.undecided > 0) ??
    steps[0]
  lastActiveKind.current = active?.kind ?? null

  const activeUndecided = active?.items.filter((i) => i.state === 'undecided') ?? []
  const activeBound = active?.items.filter((i) => i.state === 'bound') ?? []
  const activeSkipped = active?.items.filter((i) => i.state === 'skipped') ?? []

  // A step switch (or the active step rotating after a refetch, or a site
  // switch — the selection references the old site's vendor rows) drops the
  // selection and any stale failure report.
  const activeKind = active?.kind
  useEffect(() => {
    setChecked(new Set())
    setBulkFailures([])
    setBulkConfirmOpen(false)
    setQuickConfirmOpen(false)
  }, [activeKind, site])

  const bulkCreatable = active !== undefined && canCreate(system.provider, active.kind)
  const selectedItems = activeUndecided.filter((i) => checked.has(i.vendor_uid))
  const kindLabel = (kindLabels[active?.kind ?? ''] ?? active?.kind ?? 'items').toLowerCase()

  const toggle = (vendorUid: string, next: boolean) => {
    setChecked((prev) => {
      const s = new Set(prev)
      if (next) s.add(vendorUid)
      else s.delete(vendorUid)
      return s
    })
  }

  /** Select-all covers EVERY undecided row of the active step that bulk
   *  create can actually take (the full board data, not just the rendered
   *  rows) — sighted unknown-credential rows are excluded: converting a new
   *  ID needs a per-row person, so they only ever decide one by one. */
  const bulkableUndecided = activeUndecided.filter((i) => !isSightedCandidate(i))
  const allSelected =
    bulkableUndecided.length > 0 && bulkableUndecided.every((i) => checked.has(i.vendor_uid))
  const toggleAll = () => {
    if (allSelected) setChecked(new Set())
    else setChecked(new Set(bulkableUndecided.map((i) => i.vendor_uid)))
  }

  const [bulkLog, setBulkLog] = useState<string[]>([])

  /** Bulk quick-decide ("Decide 100% matches"): each selected row's
   *  100%-hint decide (quickDecideFor's priority), per-row POSTs via the
   *  runner hook. Rows without a 100% hint log as skipped and stay
   *  selected. */
  const quickRun = useMappingBulkQuickDecide(system.unique_id, site)
  const [quickConfirmOpen, setQuickConfirmOpen] = useState(false)
  const [quickTargets, setQuickTargets] = useState<QuickDecidePlan[]>([])
  const [quickLog, setQuickLog] = useState<string[]>([])
  const [quickWorking, setQuickWorking] = useState<string | null>(null)
  const quickPlans = selectedItems.map((item) => {
    const decide = quickDecideFor(system, item)
    return { item, body: decide?.body ?? null, verb: decide?.verb ?? '', foldsSiblings: decide?.foldsSiblings ?? false }
  })
  const quickableCount = quickPlans.filter((p) => p.body !== null).length

  const runQuick = () => {
    if (quickRun.isPending || quickTargets.length === 0) return
    setQuickLog([])
    setQuickWorking(null)
    quickRun.mutate(
      {
        plans: quickTargets,
        onChunk: (lines, done, total) => setQuickLog((prev) => [...prev, ...lines, `· ${done}/${total}`]),
        onProgress: setQuickWorking,
      },
      {
        onSuccess: (failures) => {
          const skippedCount = quickTargets.filter((p) => p.body === null).length
          const failedUids = new Set(failures.map((f) => f.vendor_uid))
          const fired = quickTargets.length - skippedCount
          setQuickLog((prev) => [
            ...prev,
            failures.length === 0
              ? `done — ${fired} decided${skippedCount > 0 ? `, ${skippedCount} skipped` : ''}`
              : `done — ${fired - failures.length} decided, ${failures.length} failed, ${skippedCount} skipped (failed + skipped stay selected)`,
          ])
          setBulkFailures(failures)
          // Failed AND no-match rows stay selected for individual handling.
          setChecked(
            new Set(
              quickTargets
                .filter((p) => p.body === null || failedUids.has(p.item.vendor_uid))
                .map((p) => p.item.vendor_uid),
            ),
          )
        },
      },
    )
  }

  const runBulk = () => {
    if (bulk.isPending || bulkTargets.length === 0) return
    setBulkLog([])
    bulk.mutate(
      {
        items: bulkTargets,
        onChunk: (created, failed, done, total) => {
          setBulkLog((prev) => [
            ...prev,
            `✓ created ${created} (${done}/${total})`,
            ...failed.map((f) => `✗ ${f.display} — ${f.detail}`),
          ])
        },
      },
      {
        onSuccess: (failures) => {
          setBulkLog((prev) => [
            ...prev,
            failures.length === 0
              ? `done — all ${bulkTargets.length} created`
              : `done — ${bulkTargets.length - failures.length} created, ${failures.length} failed (left selected for retry)`,
          ])
          setBulkFailures(failures)
          // Failed rows stay selected so the operator can retry just those.
          setChecked(new Set(failures.map((f) => f.vendor_uid)))
        },
      },
    )
  }

  const activeIndex = active ? steps.indexOf(active) : 0

  return (
    <Card
      title="Mapping"
      action={
        <Button variant="ghost" size="sm" onClick={onClose}>
          Back to summary
        </Button>
      }
    >
      {siteList.length > 0 && activeSite && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Site
          </span>
          {siteList.map((s) => (
            <SitePill
              key={s.site_key}
              site={s}
              active={s.site_key === activeSite.site_key}
              onClick={() => setSelectedSite(s.site_key)}
            />
          ))}
        </div>
      )}
      {board.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
        </div>
      ) : board.isError ? (
        board.error instanceof ApiError && board.error.status === 400 ? (
          <EmptyState
            title="Mapping not supported"
            hint="This provider doesn't support mapping — there is nothing to decide."
          />
        ) : (
          <EmptyState
            title="Could not load the mapping board"
            hint={errDetail(board.error)}
            action={
              <Button variant="ghost" size="sm" onClick={() => board.refetch()}>
                Retry
              </Button>
            }
          />
        )
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <p className="tnum text-[11px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              {system.name}
              {siteList.length > 0 && activeSite ? ` · ${activeSite.label} — ` : ' — '}
              Step {activeIndex + 1} of {steps.length}
            </p>
            {systemUndecided > 0 && (
              <Chip
                tone="warn"
                label={
                  (active?.undecided ?? 0) !== systemUndecided
                    ? `${active?.undecided ?? 0} in this step · ${systemUndecided} total`
                    : `${systemUndecided} to map`
                }
              />
            )}
          </div>
          {/* Plain-language FAQ for non-technical operators — collapsed by
              default, same idiom as the summary card's matching rules. */}
          <div className="rounded-sm border border-ostrel-line bg-ostrel-sand/40 px-3.5 py-2">
            <button
              type="button"
              aria-expanded={faqOpen}
              onClick={() => setFaqOpen((v) => !v)}
              className="flex w-full items-center gap-2 text-left"
            >
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                Common questions
              </span>
              <span className="ml-auto text-[11px] text-ostrel-ink-muted">
                {faqOpen ? 'Hide ▴' : 'Show ▾'}
              </span>
            </button>
            {faqOpen && (
              <dl className="mt-2 flex flex-col gap-3 border-t border-ostrel-line pt-3">
                {WORKBENCH_FAQ.map((entry) => (
                  <div key={entry.q}>
                    <dt className="text-xs font-medium text-ostrel-ink">{entry.q}</dt>
                    <dd className="mt-0.5 text-[11px] leading-relaxed text-ostrel-ink-muted">
                      {entry.a}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
          <div className="grid gap-4 md:grid-cols-[200px_1fr]">
            {/* Rail: horizontal scroll strip on mobile, vertical rail from md. */}
            <nav
              aria-label="Mapping categories"
              className="flex gap-1 overflow-x-auto pb-1 md:flex-col md:overflow-visible md:pb-0"
            >
              {steps.map((s, i) => (
                <button
                  key={s.kind}
                  type="button"
                  disabled={s.locked}
                  onClick={() => {
                    setSelectedKind(s.kind)
                    setSkipOpen(false)
                  }}
                  className={`flex shrink-0 items-center gap-2 rounded-sm border px-3 py-2 text-left transition-colors ${
                    s.kind === active?.kind
                      ? 'border-ostrel-gold bg-ostrel-sand'
                      : s.locked
                        ? 'cursor-not-allowed border-ostrel-line bg-ostrel-card opacity-60'
                        : 'border-ostrel-line bg-ostrel-card hover:bg-ostrel-sand'
                  }`}
                >
                  <span className="min-w-0 truncate text-sm font-medium text-ostrel-ink">
                    {i + 1}. {kindLabels[s.kind] ?? s.kind}
                  </span>
                  {s.locked ? (
                    <Chip tone="muted" label="Locked" className="ml-auto" />
                  ) : (
                    <span className="tnum ml-auto text-[11px] text-ostrel-ink-muted">
                      {s.decided}/{s.items.length}
                    </span>
                  )}
                </button>
              ))}
            </nav>

            <div className="flex min-w-0 flex-col gap-2">
              {active && (
                <p className="text-[11px] text-ostrel-ink-muted">{active.purpose}</p>
              )}
              {activeUndecided.length === 0 && activeBound.length === 0 && activeSkipped.length === 0 ? (
                <EmptyState
                  title={`No ${kindLabel}`}
                  hint="The system reports no vendor objects of this kind."
                />
              ) : (
                <>
                  {bulkCreatable && bulkableUndecided.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 rounded-sm border border-ostrel-line bg-ostrel-sand/40 px-3.5 py-2">
                      <Button variant="ghost" size="sm" onClick={toggleAll}>
                        {allSelected ? 'Clear' : 'Select all'}
                      </Button>
                      <span className="tnum text-[11px] text-ostrel-ink-muted">
                        {selectedItems.length} selected
                      </span>
                      <span className="tnum ml-auto text-[11px] text-ostrel-ink-muted">
                        {quickableCount > 0 && `${quickableCount} with 100% match`}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={quickableCount === 0 || quickRun.isPending || bulk.isPending}
                        onClick={() => {
                          setQuickTargets(quickPlans)
                          setQuickConfirmOpen(true)
                        }}
                      >
                        Decide 100% matches
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={selectedItems.length === 0 || bulk.isPending || quickRun.isPending}
                        onClick={() => {
                          setBulkTargets(selectedItems)
                          setBulkConfirmOpen(true)
                        }}
                      >
                        Create {selectedItems.length} selected
                      </Button>
                    </div>
                  )}
                  {active?.kind === 'group' && (activeUndecided.length > 0 || splitBusy) && (
                    <SplitOnTool
                      system={system}
                      items={selectedItems}
                      site={site}
                      onBusyChange={setSplitBusy}
                      onApplied={(failures) =>
                        // Failed rows stay selected so the operator can retry
                        // just those (same contract as the bulk create).
                        setChecked(new Set(failures.map((f) => f.vendor_uid)))
                      }
                    />
                  )}
                  {bulkFailures.length > 0 && (
                    <div className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-danger">
                        {bulkFailures.length} failed to create
                      </p>
                      <ul className="mt-1 flex flex-col gap-0.5">
                        {bulkFailures.map((f) => (
                          <li key={f.vendor_uid} className="text-[11px] text-ostrel-danger">
                            {f.display} — {f.detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {activeUndecided.map((item) => (
                    <CandidateRow
                      key={item.vendor_uid}
                      system={system}
                      item={item}
                      site={site}
                      selectable={bulkCreatable}
                      checked={checked.has(item.vendor_uid)}
                      onToggle={toggle}
                    />
                  ))}
                  {activeBound.map((item) => (
                    <CandidateRow key={item.vendor_uid} system={system} item={item} site={site} />
                  ))}
                  {activeSkipped.length > 0 && (
                    <div className="rounded-sm border border-ostrel-line bg-ostrel-sand/40">
                      <button
                        type="button"
                        onClick={() => setSkipOpen((v) => !v)}
                        className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left"
                      >
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                          Skipped
                        </span>
                        <span className="tnum text-[11px] text-ostrel-ink-muted">
                          {activeSkipped.length}
                        </span>
                        <span className="ml-auto text-[11px] text-ostrel-ink-muted">
                          {skipOpen ? 'Hide ▴' : 'Show ▾'}
                        </span>
                      </button>
                      {skipOpen && (
                        <ul className="flex flex-col gap-2 px-3.5 pb-3">
                          {activeSkipped.map((item) => (
                            <CandidateRow key={item.vendor_uid} system={system} item={item} site={site} />
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <DecisionRunModal
        open={bulkConfirmOpen}
        title={`Create ${bulkTargets.length} ${kindLabel}`}
        confirmLabel="Create"
        confirmText={
          <>
            <p>
              Create {bulkTargets.length} new {kindLabel} from the selected vendor rows:
            </p>
            <ul className="mt-2 flex max-h-48 flex-col gap-1 overflow-y-auto">
              {bulkTargets.map((i) => (
                <li key={i.vendor_uid} className="truncate text-sm text-ostrel-ink">
                  {i.display || i.vendor_uid}
                </li>
              ))}
            </ul>
          </>
        }
        log={bulkLog}
        running={bulk.isPending}
        error={bulk.error ? errDetail(bulk.error) : null}
        onConfirm={runBulk}
        onClose={() => {
          setBulkConfirmOpen(false)
          setBulkLog([])
        }}
      />
      <DecisionRunModal
        open={quickConfirmOpen}
        title={`Decide ${quickTargets.filter((p) => p.body !== null).length} 100% matches`}
        confirmLabel="Decide"
        confirmText={
          <>
            <p>Fire each row's 100%-hint decide now (attach/merge beats series/location creates):</p>
            <ul className="mt-2 flex max-h-48 flex-col gap-1 overflow-y-auto">
              {quickTargets.slice(0, 20).map((p) => (
                <li key={p.item.vendor_uid} className="truncate text-sm text-ostrel-ink">
                  {p.item.display || p.item.vendor_uid}
                  <span className="text-ostrel-ink-muted">
                    {p.body !== null ? ` — ${p.verb}` : ' — no 100% match, skipped'}
                  </span>
                </li>
              ))}
              {quickTargets.length > 20 && (
                <li className="text-[11px] text-ostrel-ink-muted">+{quickTargets.length - 20} more</li>
              )}
            </ul>
          </>
        }
        log={quickLog}
        running={quickRun.isPending}
        progress={quickWorking}
        error={quickRun.error ? errDetail(quickRun.error) : null}
        onConfirm={runQuick}
        onClose={() => {
          setQuickConfirmOpen(false)
          setQuickLog([])
        }}
      />
    </Card>
  )
}
